import React, { useState, useEffect, useRef } from 'react';

/**
 * Searchable supplier dropdown with avatar initials.
 * Modeled after SearchableCustomerSelect but for suppliers.
 */

const getInitials = (name) => {
  if (!name) return '';
  const words = name.split(' ');
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
};

const getColor = (str) => {
  const colors = ['#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#10b981'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export default function SearchableSupplierSelect({ value, suppliers, onSelect, onCreateSupplier }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      const supplier = suppliers.find((s) => s.supplier_id?.toString() === value?.toString());
      setDisplayValue(supplier ? supplier.supplier_name : '');
      setSearchTerm('');
    } else {
      setSearchTerm('');
      setDisplayValue('');
    }
  }, [value, suppliers]);

  const activeFilter = searchTerm.trim().toLowerCase();
  const filtered = activeFilter
    ? suppliers.filter(
        (s) =>
          s.supplier_name?.toLowerCase().includes(activeFilter) ||
          s.supplier_id?.toString().toLowerCase().includes(activeFilter)
      )
    : suppliers;

  const handleSelect = (supplier) => {
    setDisplayValue(supplier.supplier_name || '');
    setSearchTerm('');
    setIsOpen(false);
    onSelect?.(supplier);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const inputValue = searchTerm !== '' ? searchTerm : displayValue;

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          className="w-full pl-3 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base pointer-events-none">
        </span>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-500 text-xs p-1"
        >
          ▼
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-[400px] mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-y-auto z-[10000]">
          {typeof onCreateSupplier === 'function' && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
                onCreateSupplier?.();
              }}
              className="flex items-center gap-2 p-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 text-sm text-indigo-600"
            >
              <span className="text-base">+</span>
              <span className="font-medium">Create supplier</span>
            </div>
          )}
          {filtered.length > 0 ? (
            filtered.map((supplier) => (
              <div
                key={supplier.supplier_id}
                onClick={() => handleSelect(supplier)}
                className="flex items-center gap-3 p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50"
              >
                <div
                  className="w-8 h-8 rounded text-white flex items-center justify-center font-semibold text-xs shrink-0"
                  style={{ backgroundColor: getColor(supplier.supplier_name || '') }}
                >
                  {getInitials(supplier.supplier_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">{supplier.supplier_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">ID: {supplier.supplier_id}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-gray-500 text-sm">No suppliers found</div>
          )}
        </div>
      )}
    </div>
  );
}
